import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCodes } from 'country-list';
import styles from './style.module.scss';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import Tooltip from '@components-clean/core/tooltip';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { convertBooleanToYesNo, stringDateWithoutTimeFromUtc } from '@utils/convertor';

const AttributesArea = () => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tcommon } = useTranslation('common');
    const { current } = useContext(RiskLogContext);
    const countryCodes = useMemo(() => getCodes(), []);

    const risk = current.selectionState.selectedItem;
    const vendorList = risk?.vendors?.map((a) => a?.name).join(', ') ?? '';
    const countryList = risk?.countries?.map((a) => a?.name).join(', ') ?? '';
    const vendorInactive = risk?.vendors && risk.vendors?.some((v) => v && !v.active);
    const countryInactive = risk?.countries && risk.countries?.some((c) => !c?.code || !countryCodes.includes(c.code));

    return (
        <div className={styles.parent}>
            <div className={styles.container}>
                <div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.dateIdentified')}</span>
                        <span>{stringDateWithoutTimeFromUtc(risk?.dateIdentified, 'DD-MMM-YYYY')}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.riskStatus')}</span>
                        <span>{risk?.status}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.closedDate')}</span>
                        <span>{stringDateWithoutTimeFromUtc(risk?.closedDate, 'DD-MMM-YYYY')}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.responsibleFunction')}</span>
                        <span className={styles.inactiveFieldType}>
                            {risk?.responsibleFunction?.name}{' '}
                            {risk?.responsibleFunction && !risk?.responsibleFunction?.active && (
                                <InactiveIcon
                                    toolTipText={tcommon('common:inactiveIcon')}
                                    dataTestId="inactive-icon"
                                    tooltipPosition="bottom"
                                />
                            )}
                        </span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.owner')}</span>
                        <span className={styles.inactiveFieldType}>
                            {risk?.convertedOwner?.name}{' '}
                            {risk?.owner && !risk?.owner?.active && (
                                <InactiveIcon
                                    toolTipText={tcommon('common:inactiveIcon')}
                                    dataTestId="inactive-icon"
                                    tooltipPosition="bottom"
                                />
                            )}
                        </span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.controlStrategy')}</span>
                        <span className={styles.inactiveFieldType}>
                            {risk?.controlStrategy?.name}{' '}
                            {risk?.controlStrategy && !risk?.controlStrategy?.active && (
                                <InactiveIcon
                                    toolTipText={tcommon('common:inactiveIcon')}
                                    dataTestId="inactive-icon"
                                    tooltipPosition="bottom"
                                />
                            )}
                        </span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.category')}</span>
                        <span className={styles.inactiveFieldType}>
                            {risk?.category?.name}{' '}
                            {risk?.category && !risk?.category?.active && (
                                <InactiveIcon
                                    toolTipText={tcommon('common:inactiveIcon')}
                                    dataTestId="inactive-icon"
                                    tooltipPosition="bottom"
                                />
                            )}
                        </span>
                    </div>
                </div>
                <div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.reviewRequired')}</span>
                        <span>{convertBooleanToYesNo(risk?.reviewRequired ?? false)}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.reviewFrequency')}</span>
                        <span>{risk?.reviewFrequency}</span>
                    </div>
                    {/* Hidden but not removed as these fields will added back in at some point */}
                    {/* <div> */}
                    {/*    <span className="k-font-medium">{t('tabs.attributes.qualityIssue')}</span> */}
                    {/*    <span className={styles.inactiveFieldType}> */}
                    {/*        {risk?.qualityIssue?.name}{' '} */}
                    {/*        {risk?.qualityIssue && !risk.qualityIssue?.active && ( */}
                    {/*            <InactiveIcon */}
                    {/*                toolTipText={tcommon('common:inactiveIcon')} */}
                    {/*                dataTestId="inactive-icon" */}
                    {/*                tooltipPosition="bottom" */}
                    {/*            /> */}
                    {/*        )} */}
                    {/*    </span> */}
                    {/* </div> */}
                    {/* <div> */}
                    {/*    <span className="k-font-medium">{t('tabs.attributes.qualityIssueReference')}</span> */}
                    {/*    <span>{risk?.qualityIssueReference}</span> */}
                    {/* </div> */}
                    <div style={{ maxWidth: '600px' }}>
                        <span className="k-font-medium">{t('tabs.attributes.vendors')}</span>
                        <span className={styles.inactiveFieldType}>
                            {vendorList?.length > 1000 ? (
                                <Tooltip
                                    anchorElement="target"
                                    position="top"
                                    parentTitle
                                    style={{ maxWidth: '300px' }}
                                >
                                    <span title={vendorList}>{`${vendorList?.slice(0, 47)}...`}</span>
                                </Tooltip>
                            ) : (
                                <span>{vendorList}</span>
                            )}
                            {vendorInactive && (
                                <InactiveIcon
                                    toolTipText={tcommon('common:multipleRecordsInactiveIcon')}
                                    dataTestId="inactive-icon-vendors"
                                    tooltipPosition="bottom"
                                />
                            )}
                        </span>
                    </div>
                    <div style={{ maxWidth: '600px' }}>
                        <span className="k-font-medium">{t('tabs.attributes.countries')}</span>
                        <span className={styles.inactiveFieldType}>
                            {countryList?.length > 1000 ? (
                                <Tooltip
                                    anchorElement="target"
                                    position="top"
                                    parentTitle
                                    style={{ maxWidth: '300px' }}
                                >
                                    <span title={countryList}>{`${countryList?.slice(0, 47)}...`}</span>
                                </Tooltip>
                            ) : (
                                <span>{countryList}</span>
                            )}
                            {countryInactive && (
                                <InactiveIcon
                                    toolTipText={tcommon('common:multipleRecordsInactiveIcon')}
                                    dataTestId="inactive-icon-countries"
                                    tooltipPosition="bottom"
                                />
                            )}
                        </span>
                    </div>
                </div>
                <div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.numActiveCtQs')}</span>
                        <span>{risk?.numActiveCtQs}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.numActiveControls')}</span>
                        <span>{risk?.activeControlCount}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.numOpenObservations')}</span>
                        <span>{risk?.numOpenObservations}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.numOpenActions')}</span>
                        <span>{risk?.numOpenActions}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.updatedBy')}</span>
                        <span>{risk?.updatedBy}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.updated')}</span>
                        <span>{stringDateWithoutTimeFromUtc(risk?.updated, 'DD-MMM-YYYY')}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.createdBy')}</span>
                        <span>{risk?.createdBy}</span>
                    </div>
                    <div>
                        <span className="k-font-medium">{t('tabs.attributes.created')}</span>
                        <span>{stringDateWithoutTimeFromUtc(risk?.created, 'DD-MMM-YYYY')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttributesArea;
